import { z } from 'zod'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import { zSetting } from 'app/frontend/base/helpers/forms'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import { useTeam } from 'app/frontend/base/config/context/team'
import { activityTypes } from 'app/frontend/types/custom/ActivityType'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import EventParticipantStatus, {
  eventParticipantStatuses,
} from 'app/frontend/types/custom/EventParticipantStatus'
import EventSelectionStatus, {
  eventSelectionStatuses,
  eventSelectionStatusLabels,
} from 'app/frontend/types/custom/EventSelectionStatus'
import EventScheduleFormContent from '../EventScheduleFormContent'
import { FormGroup } from '@/components/ui/form'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { useContext, useMemo, useState } from 'react'
import { FormStateContext } from 'app/frontend/base/config/context/forms'
import { usePageInfo } from 'app/frontend/base/config/queries'
import Card from 'app/frontend/base/components/Card'
import PremiumUpsell from 'app/frontend/base/components/PremiumUpsell'
import { imageUrl } from 'app/frontend/base/helpers'
import { Button } from '@/components/ui/button'
import { Check, Pencil, Trash2 } from 'lucide-react'
import { useUpdateTeam } from 'app/frontend/base/config/mutations'
import WclGuildLinkDialog, { WclIconLink } from '../WclGuildLinkDialog'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'
import { raidsPlanningScopes } from 'app/frontend/types/custom/RaidsPlanningScope'

type EventDefaultsTeamRank = Partial<
  Pick<wowaudit.TeamRank, 'id' | 'name' | 'color'>
> & {
  default_signup_status?: EventParticipantStatus | null
  default_selection_status?: EventSelectionStatus | null
}

type EventSectionFormTeam = wowaudit.Team & {
  recurringEventsAttributes: wowaudit.RecurringEvent[]
  team_ranks_attributes: EventDefaultsTeamRank[]
}

const teamRankAttributesName = 'team_ranks_attributes'

const eventDefaultsTeamRanks = (
  teamRanks?: (Partial<wowaudit.TeamRank> | undefined)[],
): EventDefaultsTeamRank[] => {
  return (teamRanks ?? []).flatMap((rank) =>
    rank
      ? [
          {
            id: rank.id,
            name: rank.name,
            color: rank.color,
            default_signup_status: rank.default_signup_status as
              | EventParticipantStatus
              | null
              | undefined,
            default_selection_status: rank.default_selection_status as
              | EventSelectionStatus
              | null
              | undefined,
          },
        ]
      : [],
  )
}

const formSchema = z.object({
  defaultSignupStatus: z.enum(eventParticipantStatuses).nullable(),
  defaultSelectionStatus: z.enum(eventSelectionStatuses).nullable(),
  settings: z.object({
    raidsAvailableStatuses: zSetting(z.array(z.string())),
    raidsMinActivitiesForGrid: zSetting(z.number().or(z.string())),
    raidsAutoLock: zSetting(z.number().or(z.string()).or(z.null())),
    raidsAllowGuests: zSetting(z.string()),
    raidsSpreadRoleAssignments: zSetting(z.boolean()),
  }),
  recurringEventsAttributes: z.array(
    z
      .object({
        id: z.number().optional(),
        weekDay: z.string().regex(/^[0-6]$/),
        repeatFrom: z.string().min(1),
        repeatUntil: z.string().optional().or(z.null()),
        startTime: z.string().min(1),
        finishTime: z.string().min(1),
        optional: z.boolean(),
        activityType: z.enum(activityTypes),
        instanceId: z.string().or(z.null()).or(z.number()),
        difficulty: z.enum(difficulties),
        title: z.string().nullable().optional(),
        autoAddParticipants: z.boolean(),
        lockHoursBeforeStart: z.number().or(z.null()).or(z.string()),
        lockAtPresentCount: z.number().or(z.null()).or(z.string()),
        defaultSignupStatus: z.enum(eventParticipantStatuses).nullable(),
        defaultSelectionStatus: z.enum(eventSelectionStatuses).nullable(),
        defaultRaidsPlanningScope: z.enum(raidsPlanningScopes).nullable(),
        guestPolicy: z.string().min(1).or(z.null()),
        _destroy: z.boolean().optional(),
      })
      .superRefine((value, ctx) => {
        if (value.id !== undefined) return
        if (value.repeatFrom >= format(new Date(), 'yyyy-MM-dd')) return
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid date',
          path: ['repeatFrom'],
        })
      })
      .superRefine((value, ctx) => {
        if (!value.repeatUntil) return

        const today = format(new Date(), 'yyyy-MM-dd')
        if (value.id === undefined && value.repeatUntil < today) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid date',
            path: ['repeatUntil'],
          })
        }

        if (value.repeatUntil < value.repeatFrom) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid date',
            path: ['repeatUntil'],
          })
        }
      }),
  ),
  team_ranks_attributes: z.array(
    z.object({
      id: z.number().optional(),
      name: z.string().optional(),
      color: z.string().nullable().optional(),
      default_signup_status: z.enum(eventParticipantStatuses).or(z.null()),
      default_selection_status: z.enum(eventSelectionStatuses).or(z.null()),
    }),
  ),
})

const EventRankDefaultsTable = () => {
  const { watch } = useFormContext()
  const teamRanks: EventDefaultsTeamRank[] = watch(teamRankAttributesName)

  const columns: ColumnDef<EventDefaultsTeamRank>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'By team rank',
        cell: (info) => {
          return (
            <span
              className='flex min-h-10 items-center whitespace-nowrap ml-2'
              style={{ color: info.row.original.color ?? undefined }}
            >
              {info.row.original.name}
            </span>
          )
        },
      },
      {
        id: 'default_signup_status',
        header: () => (
          <span className='whitespace-nowrap'>Default signup status</span>
        ),
        cell: (info) => {
          return (
            <SelectField<EventParticipantStatus>
              layout='stacked'
              name={`${teamRankAttributesName}.${info.row.index}.default_signup_status`}
              options={eventParticipantStatuses}
              customLabel={''}
              isClearable
              customPlaceholder='Use team default'
            />
          )
        },
      },
      {
        id: 'default_selection_status',
        header: () => (
          <span className='whitespace-nowrap'>Default selection status</span>
        ),
        cell: (info) => {
          return (
            <SelectField<EventSelectionStatus>
              layout='stacked'
              name={`${teamRankAttributesName}.${info.row.index}.default_selection_status`}
              options={eventSelectionStatuses}
              optionLabelFn={(status) => eventSelectionStatusLabels[status]}
              customLabel={''}
              isClearable
              customPlaceholder='Use team default'
            />
          )
        },
      },
    ],
    [],
  )

  return (
    <div className='mt-4 mb-6'>
      <DataTable
        cellClassName='align-top px-2'
        includeHeader={teamRanks && teamRanks.length > 0}
        columns={columns}
        data={teamRanks ?? []}
      />
      <p className='text-sm mt-1 text-muted-foreground'>
        Defaults specified on (recurring and manual) event level take precedence
        over these
      </p>
    </div>
  )
}

const EventSection = () => {
  const { handleSectionChange } = useContext(FormStateContext)
  const { t } = useTranslation()
  const { team, guild } = useTeam()
  const queryClient = useQueryClient()
  const {
    data: { user },
  } = usePageInfo()
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const { mutate: updateTeam, isPending: linkingGuild } = useUpdateTeam(team)

  const wclLinkedAccount = user?.linkedAccounts?.find(
    (account) => account.displayType === 'Warcraft Logs',
  )
  const hasLinkedWclGuild = !!team.wclGuildId && !!team.wclLinkedAccountId

  return (
    <FormWrapper<EventSectionFormTeam>
      key={team?.id}
      formSchema={formSchema}
      entity={{
        ...team,
        recurringEventsAttributes: team.recurringEvents ?? [],
        team_ranks_attributes: eventDefaultsTeamRanks(team.teamRanks),
      }}
      onSave={async (form, data) => {
        await queryClient.invalidateQueries({
          queryKey: ['teamEvents', { id: team.id }],
        })
        form.reset({
          ...data,
          recurringEventsAttributes: data.recurringEvents ?? [],
          team_ranks_attributes: eventDefaultsTeamRanks(data.teamRanks),
        })
      }}
      transformValues={(values) => {
        return {
          ...values,
          team_ranks_attributes: values.team_ranks_attributes.map(
            (rank: EventDefaultsTeamRank) => ({
              id: rank.id,
              default_signup_status: rank.default_signup_status,
              default_selection_status: rank.default_selection_status,
            }),
          ),
        }
      }}
      entityType='team'
    >
      <EventScheduleFormContent />

      <SettingField name='raidsAvailableStatuses' />

      <SettingField name='raidsMinActivitiesForGrid' layout='box' />

      <h5>Event insights</h5>

      <p className='mb-6'>
        By connecting your Warcraft Logs guild, your events will be enhanced
        with valuable insights. Your private report data will not be exposed to
        the public, insights are only visible to team members. Report data for
        new events will only be imported if your guild is at least{' '}
        <Link
          to={`${team.path}/settings#premium`}
          className='text-quality-epic hover:text-quality-epic/90'
          onClick={() => handleSectionChange('premium')}
        >
          Epic
        </Link>{' '}
        tier.
      </p>

      <Card className='py-3 px-4 mt-2 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-2'>
        <div className='flex items-center gap-4 min-w-0'>
          <img src={imageUrl('wcl-logo.png')} className='w-12' />
          <div className='min-w-0'>
            <span className='font-bold text-lg'>Warcraft Logs</span>
            <div className='grow mr-6 text-sm truncate'>
              {team.wclGuildName ? (
                <span className='flex items-center gap-2'>
                  {team.wclGuildName}{' '}
                  <WclIconLink id={team.wclGuildId as number} />
                </span>
              ) : (
                'No guild linked'
              )}
            </div>
          </div>
        </div>

        {hasLinkedWclGuild ? (
          <div className='flex items-center gap-2 shrink-0'>
            <Button
              type='button'
              className='w-28 pointer-events-none border-success-foreground'
              variant='outline'
            >
              <span className='flex gap-2 items-center text-success-foreground'>
                <Check size={14} />
                Linked
              </span>
            </Button>
            <Button
              type='button'
              variant='outline'
              size='icon'
              onClick={() => setLinkDialogOpen(true)}
              aria-label='Edit linked guild'
              title='Edit linked guild'
            >
              <Pencil size={14} />
            </Button>
            <Button
              type='button'
              variant='outline'
              size='icon'
              onClick={() =>
                updateTeam({
                  wclGuildId: null,
                  wclGuildName: null,
                  wclLinkedAccountId: null,
                })
              }
              aria-label='Unlink guild'
              title='Unlink guild'
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ) : wclLinkedAccount ? (
          <Button
            type='button'
            variant='outline'
            className='w-28'
            onClick={() => setLinkDialogOpen(true)}
          >
            Link guild
          </Button>
        ) : (
          <span className='flex items-center gap-2 text-muted-foreground italic'>
            <span>
              <Link
                className='text-primary hover:text-primary/90'
                to='/profile'
              >
                Link
              </Link>{' '}
              your user account first
            </span>
          </span>
        )}
      </Card>

      {hasLinkedWclGuild && guild.premiumPoints < 15 ? (
        <PremiumUpsell
          className='mb-6'
          title={t('errors.custom_domain.not_premium_title')}
          description={
            <>
              New events will not be imported from Warcraft Logs until your
              guild is at least <span className='text-quality-epic'>Epic</span>{' '}
              tier.
            </>
          }
          to={`${team.path}/settings#premium`}
          onNavigate={() => handleSectionChange('premium')}
        />
      ) : null}

      <WclGuildLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        isLinking={linkingGuild}
        onLink={(selectedGuild) => {
          if (!wclLinkedAccount) return
          updateTeam(
            {
              wclGuildId: selectedGuild.id,
              wclGuildName: selectedGuild.name,
              wclLinkedAccountId: wclLinkedAccount.id,
            },
            {
              onSuccess: () => setLinkDialogOpen(false),
            },
          )
        }}
      />

      <h5>Advanced defaults</h5>

      <p className='mb-6'>
        The options listed below can be overridden when creating a recurring or
        one-time event (under the advanced options). Changing them here will not
        affect existing (recurring and one-time) events that specified their own
        defaults explicitly. These settings will also apply to events created
        using the API or Discord bot.
      </p>

      <FormGroup>
        <SelectField
          name='defaultSignupStatus'
          options={eventParticipantStatuses}
        />
        <SelectField
          name='defaultSelectionStatus'
          options={eventSelectionStatuses}
          optionLabelFn={(status) => eventSelectionStatusLabels[status]}
        />
      </FormGroup>

      <EventRankDefaultsTable />

      <SettingField name='raidsAllowGuests' />
      <SettingField name='raidsAutoLock' layout='box' />

      <h5>Raid plans</h5>
      <SettingField name='raidsSpreadRoleAssignments' layout='box' />
    </FormWrapper>
  )
}

export default EventSection
